import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastAction } from '../hooks/useToastAction';
import { useOpenRequest } from '../hooks/useOpenRequest';
import {
  Table,
  type Column,
  type SelectionConfig,
  Badge,
  Button,
  ContactOverlay,
  VenueModal,
  VenueFieldEditor,
  type VenueFormData,
  ConcertModal,
  type ConcertFormData,
  TodoModal,
  type TodoFormData,
  TableSkeleton,
  TableLoadingOverlay,
  Pagination,
  SearchInput,
  AddressColumnFilter,
  DateRangeFilter,
  InterestColumnFilter,
  ColumnFilter,
  type ContextMenuItem,
  SaveViewModal,
  EditViewModal,
  ReportMistakeModal,
  ConfirmDialog,
  AddVenuesToMailingListModal,
  type MailingListAddOptions,
  MailingListPickerModal,
  MailingListFormModal,
  Tooltip,
  VenueFlagMenu,
  type FlagFilterValue,
  VenueHiddenMenu,
  type HiddenFilterValue,
  InterestModal,
} from '../components/Common';
import { venuesAPI, contactsAPI, bandsAPI, concertsAPI, venueViewsAPI, mailingListsAPI } from '../api/endpoints';
import { useVenueSearch, isDefaultStatusFilter } from '../hooks/useVenueSearch';
import { useLatestView } from '../hooks/useLatestView';
import type { Venue, Contact, SlimContact, VenueTableRow, Address, MonthPeriod, Month, VenueView, VenueViewFilters, PaginatedResponse, MailingList, MailingListContact, VenueFilter, RecontactRequest } from '../types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import clsx from 'clsx';
import { MessageSquare, Plus, Pencil, Bell, ArrowUpRight, ArrowDownLeft, Star, ExternalLink, Save, Upload, Music, Beer, Guitar, UtensilsCrossed, Hotel, Building2, Tent, Users, Landmark, HelpCircle, Globe, MousePointerClick, Info, Settings, PartyPopper, Trophy, GraduationCap, Briefcase, AlertTriangle, Check, CheckSquare, Mail, MailPlus, Trash2, X, MapPin, Rows3, Flag, FlagOff, Hourglass, History } from 'lucide-react';
import { format } from 'date-fns';
import { recencyColor, recencyLabel } from '../utils/recencyColor';
import { LAST_CONTACT_SOURCE_NOTE } from '../utils/lastContact';
import { patchCachedVenueFlags, refreshCachedVenues } from '../utils/venueFlags';
import { isPaused, recontactDate, toApiParams } from '../utils/venuePause';
import { displayUrl, externalUrl } from '../utils/externalUrl';

const SaxophoneIcon = ({ size = 24, className }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14 2l-4 4 8 8 4-4z" />
    <path d="M10 6L4 12c-2 2-2 5 0 7s5 2 7 0l6-6" />
    <circle cx="8" cy="16" r="1" />
    <circle cx="6" cy="14" r="1" />
    <circle cx="10" cy="18" r="1" />
  </svg>
);

// One toast for the row flag toggle, shared with the overlay's so fast toggles do not stack
const ROW_FLAG_TOAST_ID = 'venue-flag';

const VENUE_TYPE_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  concert_hall: Music,
  bar: Beer,
  concert_bar: Guitar,
  jazz_club: SaxophoneIcon,
  restaurant: UtensilsCrossed,
  hotel: Hotel,
  city_concert_hall: Building2,
  festival: Tent,
  association: Users,
  city: Landmark,
  comite_des_fetes: PartyPopper,
  competition: Trophy,
  mentoring: GraduationCap,
  booking_agency: Briefcase,
  other: HelpCircle,
};

const VENUE_TYPE_FILTER_VALUES = [
  { value: 'concert_hall', label: 'Concert Hall' },
  { value: 'bar', label: 'Bar' },
  { value: 'concert_bar', label: 'Concert Bar' },
  { value: 'jazz_club', label: 'Jazz Club' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'city_concert_hall', label: 'City Concert Hall' },
  { value: 'festival', label: 'Festival' },
  { value: 'association', label: 'Association' },
  { value: 'city', label: 'City' },
  { value: 'comite_des_fetes', label: 'Comité des fêtes' },
  { value: 'competition', label: 'Competition' },
  { value: 'mentoring', label: 'Mentoring' },
  { value: 'booking_agency', label: 'Booking Agency' },
  { value: 'other', label: 'Other' },
];


const VENUE_STATUS_FILTER_VALUES = [
  { value: 'open', label: 'Open' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'no_concerts', label: 'No Concerts' },
  { value: 'closed', label: 'Closed' },
];

const VENUE_VISIBILITY_FILTER_VALUES = [
  { value: 'private', label: 'Private' },
  { value: 'public', label: 'Public' },
  { value: 'pending_public', label: 'Pending' },
];

// Contact-state filter values for the contacts column. Grouped: active states,
// composite venue states, then individual contact flags. Keys map 1:1 to the
// backend `contact_filter` predicates.
const CONTACT_FILTER_VALUES = [
  { value: 'active', label: 'Active Contact' },
  { value: 'active_email', label: 'Active Contact with Email' },
  { value: 'active_phone', label: 'Active Contact with Phone' },
  { value: '__sep1__', label: '', separator: true },
  { value: 'gatekeeper_only', label: 'Gatekeeper Only' },
  { value: 'no_contact', label: 'No Contact' },
  { value: 'no_active', label: 'No Active Contact' },
  { value: '__sep2__', label: '', separator: true },
  { value: 'gatekeeper', label: 'Gatekeeper' },
  { value: 'do_not_contact', label: 'Do Not Contact' },
  { value: 'expired', label: 'Expired' },
  { value: 'auto_discovered', label: 'Auto Discovered' },
];

const COMM_COUNT_FILTER_VALUES = [
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4+', label: '4+' },
];

const MONTH_LABELS: Record<string, string> = {
  january: 'Jan',
  february: 'Feb',
  march: 'Mar',
  april: 'Apr',
  may: 'May',
  june: 'Jun',
  july: 'Jul',
  august: 'Aug',
  september: 'Sep',
  october: 'Oct',
  november: 'Nov',
  december: 'Dec',
};

const ALL_MONTHS_FILTER_VALUES = [
  { value: 'now', label: 'Now' },
  { value: 'january', label: 'January' },
  { value: 'february', label: 'February' },
  { value: 'march', label: 'March' },
  { value: 'april', label: 'April' },
  { value: 'may', label: 'May' },
  { value: 'june', label: 'June' },
  { value: 'july', label: 'July' },
  { value: 'august', label: 'August' },
  { value: 'september', label: 'September' },
  { value: 'october', label: 'October' },
  { value: 'november', label: 'November' },
  { value: 'december', label: 'December' },
];

// Contact period also offers "No Info" — venues with no ideal contact period specified
const CONTACT_PERIOD_FILTER_VALUES = [
  { value: 'no_info', label: 'No Info' },
  ...ALL_MONTHS_FILTER_VALUES,
];

const MONTH_ORDER: Month[] = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const formatMonthPeriod = (period: MonthPeriod | undefined): string => {
  if (!period) return '-';
  if (period.all_year) return 'All Year';
  if (period.months.length === 0) return '-';

  // Sort months by calendar order
  const indices = period.months
    .map(m => MONTH_ORDER.indexOf(m))
    .filter(i => i !== -1)
    .sort((a, b) => a - b);

  if (indices.length === 1) {
    return MONTH_LABELS[MONTH_ORDER[indices[0]]];
  }

  // Group into consecutive runs, allowing wrap-around (e.g. Nov,Dec,Jan,Feb)
  // First, check if all indices form a single circular run
  const indexSet = new Set(indices);
  // Find the first index that is NOT preceded (circularly) by the previous month
  let startIdx = -1;
  for (const idx of indices) {
    const prev = (idx - 1 + 12) % 12;
    if (!indexSet.has(prev)) {
      startIdx = idx;
      break;
    }
  }

  // Walk from startIdx circularly and build consecutive groups
  const groups: number[][] = [];
  let current: number[] = [];
  let walk = startIdx;
  const visited = new Set<number>();

  for (let i = 0; i < indices.length; i++) {
    if (!indexSet.has(walk)) {
      // Gap found — find next index
      if (current.length > 0) {
        groups.push(current);
        current = [];
      }
      // Find the next present index after walk
      for (let j = 1; j <= 12; j++) {
        const next = (walk + j) % 12;
        if (indexSet.has(next) && !visited.has(next)) {
          walk = next;
          break;
        }
      }
    }
    current.push(walk);
    visited.add(walk);
    walk = (walk + 1) % 12;
  }
  if (current.length > 0) {
    groups.push(current);
  }

  return groups.map(group => {
    if (group.length === 1) {
      return MONTH_LABELS[MONTH_ORDER[group[0]]];
    }
    return `${MONTH_LABELS[MONTH_ORDER[group[0]]]} to ${MONTH_LABELS[MONTH_ORDER[group[group.length - 1]]]}`;
  }).join(', ');
};

// Column key to display label for the visibility settings
const COLUMN_LABELS: Record<string, string> = {
  favorites: 'Favorites',
  type: 'Type',
  styles: 'Style',
  url: 'URL',
  name: 'Venue Name',
  commentary: 'Commentary',
  address: 'Address',
  concert_period: 'Concert Period',
  sent_count: 'Sent',
  received_count: 'Received',
  last_contact_date: 'Last Contact',
  interests: 'Interest',
  contacts: 'Contacts',
  ideal_contact_period: 'Contact Period',
  source: 'Source',
  recontact_after: 'Recontact From',
};

/**
 * Columns hidden until the user asks for them in the column menu.
 *
 * `recontact_after` is a prospecting-queue sort rather than day-to-day information: the Interest
 * column already badges a paused venue and the toolbar hourglass filters on it, so the column only
 * widens the table for most sessions. See docs/VENUE_PAUSE.md.
 */
const DEFAULT_HIDDEN_COLUMNS = ['recontact_after'];

/**
 * The key is versioned because a stored list predating a new entry in DEFAULT_HIDDEN_COLUMNS cannot
 * say whether the column was wanted or simply did not exist yet. Bumping the suffix seeds the old
 * list with the new defaults once; everything after that is the user's own choice.
 */
const HIDDEN_COLUMNS_KEY = 'venues-hidden-columns-v2';
const LEGACY_HIDDEN_COLUMNS_KEY = 'venues-hidden-columns';

function readHiddenColumns(): string[] {
  try {
    const stored = localStorage.getItem(HIDDEN_COLUMNS_KEY);
    if (stored) return JSON.parse(stored);
    const legacy = localStorage.getItem(LEGACY_HIDDEN_COLUMNS_KEY);
    const seeded = [...new Set<string>([...(legacy ? JSON.parse(legacy) : []), ...DEFAULT_HIDDEN_COLUMNS])];
    localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(seeded));
    return seeded;
  } catch {
    return [...DEFAULT_HIDDEN_COLUMNS];
  }
}

// Map column keys to the filter param keys they use
const COLUMN_FILTER_PARAMS: Record<string, string[]> = {
  favorites: ['favorites'],
  type: ['type'],
  styles: ['style'],
  name: ['status'],
  address: ['address_filter'],
  concert_period: ['concert_period'],
  sent_count: ['sent_count'],
  received_count: ['received_count'],
  last_contact_date: ['last_contact_after', 'last_contact_before', 'last_contact_never'],
  interests: ['interest_level', 'interest_band'],
  contacts: ['contact_filter'],
  ideal_contact_period: ['ideal_contact_period'],
  source: ['source'],
  // `recontact_after` is deliberately absent: its `paused` filter always has a value (it defaults
  // to hiding), and the toolbar hourglass always shows it, so hiding the column hides nothing.
};

export function VenuesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { run } = useToastAction();
  const { user } = useAuth();
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  // The pause picker: one row from its context menu, or the whole selection (docs/VENUE_PAUSE.md)
  const [pauseModalRow, setPauseModalRow] = useState<VenueTableRow | null>(null);
  const [isPauseBulkOpen, setIsPauseBulkOpen] = useState(false);
  const [selectedVenueContacts, setSelectedVenueContacts] = useState<Contact[]>([]);
  const [isContactOverlayOpen, setIsContactOverlayOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | undefined>(undefined);
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [venueModalMode, setVenueModalMode] = useState<'create' | 'edit'>('create');
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [todoVenue, setTodoVenue] = useState<Venue | null>(null);
  const [isConcertModalOpen, setIsConcertModalOpen] = useState(false);
  const [concertVenue, setConcertVenue] = useState<Venue | null>(null);
  const [isReportMistakeOpen, setIsReportMistakeOpen] = useState(false);
  const [reportVenue, setReportVenue] = useState<Venue | null>(null);
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
  const [editingView, setEditingView] = useState<VenueView | null>(null);
  const [confirmedViewId, setConfirmedViewId] = useState<string | null>(null);
  const [pendingViewId, setPendingViewId] = useState<string | null>(null);
  const [isQuickEditEnabled, setIsQuickEditEnabled] = useState(() => localStorage.getItem('venues-quick-edit') === 'true');
  const [quickEditState, setQuickEditState] = useState<{ venue: Venue; field: string; position: { x: number; y: number } } | null>(null);
  const [isCompactMode, setIsCompactMode] = useState(() => localStorage.getItem('venues-compact-mode') === 'true');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(readHiddenColumns);
  // Multi-select state
  const [isMultiMode, setIsMultiMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allPagesSelected, setAllPagesSelected] = useState(false);
  const [isBatchDeleteConfirmOpen, setIsBatchDeleteConfirmOpen] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  const [isResetFlagsConfirmOpen, setIsResetFlagsConfirmOpen] = useState(false);
  const [isResettingFlags, setIsResettingFlags] = useState(false);
  // Add-to-mailing-list flow
  const [mlStep, setMlStep] = useState<'closed' | 'config' | 'create' | 'existing'>('closed');
  const [mlResolving, setMlResolving] = useState(false);
  const [mlSubmitting, setMlSubmitting] = useState(false);
  const [mlContacts, setMlContacts] = useState<MailingListContact[]>([]);

  // Overlay navigation state
  // Filling the overlay waits on a contact fetch; this retires one the user closed meanwhile
  const openRequest = useOpenRequest();
  const [overlayAbsoluteIndex, setOverlayAbsoluteIndex] = useState<number | null>(null);
  const [pendingNavIndex, setPendingNavIndex] = useState<number | null>(null);
  const [isOverlayNavigating, setIsOverlayNavigating] = useState(false);

  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const columnSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const columnSettingsDropdownRef = useRef<HTMLDivElement>(null);
  const [columnSettingsPosition, setColumnSettingsPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isColumnSettingsPositioned, setIsColumnSettingsPositioned] = useState(false);
  const [isVisibilityFilterOpen, setIsVisibilityFilterOpen] = useState(false);
  const visibilityFilterButtonRef = useRef<HTMLButtonElement>(null);
  const visibilityFilterDropdownRef = useRef<HTMLDivElement>(null);
  const [visibilityFilterPosition, setVisibilityFilterPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isVisibilityFilterPositioned, setIsVisibilityFilterPositioned] = useState(false);

  // Use the new venue search hook for server-side pagination/search/filter
  const {
    venues,
    pagination,
    isLoading: isLoadingVenues,
    isFetching,
    error: venuesError,
    params,
    setSearch: applySearch,
    setFilter: applyFilter,
    setFilters: applyFilters,
    setSort: applySort,
    setPage,
    resetFilters,
    setAllParams,
    refetch: refetchVenues,
  } = useVenueSearch();

  // Surface load failures as a toast — otherwise a failed query renders as an
  // empty table ("No venues found"), which is indistinguishable from a genuine
  // no-results state. Keyed on the error object so it fires once per failure.
  useEffect(() => {
    if (venuesError) {
      toast.error('Failed to load venues. Please try again.', { id: 'venues-load-error' });
    }
  }, [venuesError]);

  // Fetch venue views
  const { data: venueViews = [], refetch: refetchViews } = useQuery({
    queryKey: ['venue-views'],
    queryFn: async () => {
      const response = await venueViewsAPI.list();
      return response.data;
    },
    staleTime: 60000,
  });

  const getCurrentFilters = useCallback((): VenueViewFilters => {
    const filters = Object.fromEntries(
      Object.entries(params).filter(([k, v]) => v !== undefined && k !== 'page' && k !== 'page_size')
    ) as VenueViewFilters;
    if (hiddenColumns.length > 0) {
      filters.hidden_columns = hiddenColumns;
    }
    return filters;
  }, [params, hiddenColumns]);

  const filtersMatch = useCallback((a: VenueViewFilters, b: VenueViewFilters) => {
    // Compare string filter params
    const aRecord = a as Record<string, unknown>;
    const bRecord = b as Record<string, unknown>;
    const allKeys = new Set([...Object.keys(aRecord), ...Object.keys(bRecord)]);
    for (const key of allKeys) {
      if (key === 'hidden_columns') continue;
      if ((aRecord[key] || undefined) !== (bRecord[key] || undefined)) return false;
    }
    // Compare hidden_columns arrays
    const aHidden = a.hidden_columns || [];
    const bHidden = b.hidden_columns || [];
    if (aHidden.length !== bHidden.length) return false;
    const aSorted = [...aHidden].sort();
    const bSorted = [...bHidden].sort();
    return aSorted.every((v, i) => v === bSorted[i]);
  }, []);

  // The reserved "Latest" view: rewritten on every filter the user picks by hand, so the state
  // they last filtered into is always one chip away. See docs/LATEST_VIEW.md.
  const latestView = useMemo(() => venueViews.find(v => v.is_latest) ?? null, [venueViews]);
  const namedViews = useMemo(() => venueViews.filter(v => !v.is_latest), [venueViews]);

  const saveLatestView = useCallback(async (filters: VenueViewFilters) => {
    const response = await venueViewsAPI.saveLatest(filters);
    // Patch the cache instead of refetching: the one chip that changed is in the response.
    queryClient.setQueryData<VenueView[]>(['venue-views'], (old) =>
      old ? [...old.filter(v => v._id !== response.data._id), response.data] : old
    );
  }, [queryClient]);

  const { markChange: markFilterChange, cancelPendingChange: cancelPendingLatest } = useLatestView({
    filters: getCurrentFilters(),
    latestFilters: latestView?.filters ?? null,
    save: saveLatestView,
  });

  // Every filter the user picks by hand goes through one of these wrappers, and that is what tells
  // the "Latest" view to follow. The page's own filter writes — applying a view, Reset to All —
  // deliberately do not, or every click on a view would overwrite Latest with that view.
  const setSearch = useCallback((q: string) => {
    markFilterChange('search');
    applySearch(q);
  }, [markFilterChange, applySearch]);
  const setFilter = useCallback((key: string, value: string | undefined) => {
    markFilterChange();
    applyFilter(key, value);
  }, [markFilterChange, applyFilter]);
  const setFilters = useCallback((filters: Record<string, string | undefined>) => {
    markFilterChange();
    applyFilters(filters);
  }, [markFilterChange, applyFilters]);
  const setSort = useCallback((sortBy: string, sortOrder: 'asc' | 'desc' | null) => {
    markFilterChange();
    applySort(sortBy, sortOrder);
  }, [markFilterChange, applySort]);

  // Derive activeViewId from current state instead of syncing via effect
  const activeViewId = useMemo(() => {
    const currentFilters = getCurrentFilters();
    // Check confirmed view first - if filters still match, it's active
    if (confirmedViewId) {
      const viewFilters = venueViews.find(v => v._id === confirmedViewId)?.filters;
      if (viewFilters && filtersMatch(currentFilters, viewFilters)) {
        return confirmedViewId;
      }
    }
    // Check pending view - if filters have synced, it's now active
    if (pendingViewId) {
      const viewFilters = venueViews.find(v => v._id === pendingViewId)?.filters;
      if (viewFilters && filtersMatch(currentFilters, viewFilters)) {
        return pendingViewId;
      }
    }
    return null;
  }, [confirmedViewId, pendingViewId, getCurrentFilters, venueViews, filtersMatch]);

  const isUnsavedView = useMemo(() => {
    const currentFilters = getCurrentFilters();
    // The page's own defaults — the open and unknown venues, and the pause hiding the venues that
    // asked not to be written to yet — are not filters the user picked, so on their own they do not
    // turn the page into an unsaved view worth nagging about.
    const hasActiveFilters = Object.entries(currentFilters).some(
      ([key, value]) =>
        value !== undefined &&
        !(key === 'status' && isDefaultStatusFilter(value)) &&
        !(key === 'paused' && value === 'false'),
    );
    const effectiveViewId = activeViewId || pendingViewId;
    // "Latest" follows the filters around on its own, so it is not a view the user saved: the nag
    // is about the named views. See docs/LATEST_VIEW.md.
    const hasNamedView = !!effectiveViewId && effectiveViewId !== latestView?._id;
    return !hasNamedView && hasActiveFilters;
  }, [getCurrentFilters, activeViewId, pendingViewId, latestView]);

  const saveViewButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isUnsavedView) {
      saveViewButtonRef.current?.focus();
    }
  }, [isUnsavedView]);

  const handleApplyView = (view: VenueView) => {
    cancelPendingLatest();
    setPendingViewId(view._id);
    setConfirmedViewId(null);
    const { hidden_columns, ...filterParams } = view.filters;
    setAllParams(filterParams as Record<string, string | undefined>);
    const newHidden = hidden_columns || [];
    setHiddenColumns(newHidden);
    localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(newHidden));
  };

  const handleResetToAll = () => {
    cancelPendingLatest();
    setConfirmedViewId(null);
    setPendingViewId(null);
    resetFilters();
    setHiddenColumns([...DEFAULT_HIDDEN_COLUMNS]);
    localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(DEFAULT_HIDDEN_COLUMNS));
  };

  const handleSaveView = async (name: string, filters: VenueViewFilters) => {
    const filtersWithColumns: VenueViewFilters = {
      ...filters,
      hidden_columns: hiddenColumns.length > 0 ? hiddenColumns : undefined,
    };
    const response = await venueViewsAPI.create({ name, filters: filtersWithColumns });
    await refetchViews();
    setConfirmedViewId(response.data._id);
    setPendingViewId(null);
  };

  const handleUpdateViewFilters = async (id: string, filters: VenueViewFilters) => {
    const filtersWithColumns: VenueViewFilters = {
      ...filters,
      hidden_columns: hiddenColumns.length > 0 ? hiddenColumns : undefined,
    };
    await venueViewsAPI.update(id, { filters: filtersWithColumns });
    await refetchViews();
    setConfirmedViewId(id);
    setPendingViewId(null);
  };

  const handleRenameView = async (id: string, newName: string) => {
    await venueViewsAPI.update(id, { name: newName });
    await refetchViews();
  };

  const handleDeleteView = async (id: string) => {
    await venueViewsAPI.delete(id);
    await refetchViews();
    if (confirmedViewId === id) {
      setConfirmedViewId(null);
    }
    if (pendingViewId === id) {
      setPendingViewId(null);
    }
  };

  // Drag-and-drop reordering state
  const [draggedViewId, setDraggedViewId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{ viewId: string; side: 'left' | 'right' } | null>(null);

  const handleDragStart = (e: React.DragEvent, viewId: string) => {
    setDraggedViewId(viewId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', viewId);
  };

  const handleDragOver = (e: React.DragEvent, viewId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (viewId === draggedViewId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const side = e.clientX < midX ? 'left' : 'right';
    setDropIndicator({ viewId, side });
  };

  const handleDragLeave = () => {
    setDropIndicator(null);
  };

  const getInsertIndex = (targetViewId: string, side: 'left' | 'right') => {
    const currentOrder = namedViews.map(v => v._id);
    const dragIdx = currentOrder.indexOf(draggedViewId!);
    let dropIdx = currentOrder.indexOf(targetViewId);
    if (side === 'right') dropIdx++;
    // Adjust for removal of dragged item
    if (dragIdx < dropIdx) dropIdx--;
    return dropIdx;
  };

  const handleDrop = async (e: React.DragEvent, targetViewId: string) => {
    e.preventDefault();
    const side = dropIndicator?.viewId === targetViewId ? dropIndicator.side : 'left';
    setDropIndicator(null);
    if (!draggedViewId || draggedViewId === targetViewId) {
      setDraggedViewId(null);
      return;
    }
    const currentOrder = namedViews.map(v => v._id);
    const dragIdx = currentOrder.indexOf(draggedViewId);
    if (dragIdx === -1) return;
    currentOrder.splice(dragIdx, 1);
    const insertIdx = getInsertIndex(targetViewId, side);
    currentOrder.splice(insertIdx, 0, draggedViewId);
    setDraggedViewId(null);
    // Optimistic update via query cache
    queryClient.setQueryData(['venue-views'], (old: VenueView[] | undefined) => {
      if (!old) return old;
      const map = new Map(old.map(v => [v._id, v]));
      const reordered = currentOrder.map((id, i) => ({ ...map.get(id)!, display_order: i }));
      // Latest is not in the dragged order — keep it, or the chip would vanish until the refetch.
      const latest = old.find(v => v.is_latest);
      return latest ? [...reordered, latest] : reordered;
    });
    await venueViewsAPI.reorder(currentOrder);
    await refetchViews();
  };

  const handleDragEnd = () => {
    setDraggedViewId(null);
    setDropIndicator(null);
  };

  // Position column settings dropdown
  const openColumnSettings = useCallback(() => {
    if (!columnSettingsButtonRef.current) return;
    const rect = columnSettingsButtonRef.current.getBoundingClientRect();
    const dropdownWidth = 220;
    const viewportWidth = window.innerWidth;
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth - 8) {
      left = Math.max(8, rect.right - dropdownWidth);
    }
    const top = rect.bottom + 4;
    setColumnSettingsPosition({ top, left });
    setIsColumnSettingsPositioned(true);
    setIsColumnSettingsOpen(true);
  }, []);

  // Close column settings dropdown when clicking outside
  useEffect(() => {
    if (!isColumnSettingsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        columnSettingsButtonRef.current && !columnSettingsButtonRef.current.contains(target) &&
        columnSettingsDropdownRef.current && !columnSettingsDropdownRef.current.contains(target)
      ) {
        setIsColumnSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isColumnSettingsOpen]);

  // Position visibility filter dropdown
  const openVisibilityFilter = useCallback(() => {
    if (!visibilityFilterButtonRef.current) return;
    const rect = visibilityFilterButtonRef.current.getBoundingClientRect();
    const dropdownWidth = 180;
    const viewportWidth = window.innerWidth;
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth - 8) {
      left = Math.max(8, rect.right - dropdownWidth);
    }
    const top = rect.bottom + 4;
    setVisibilityFilterPosition({ top, left });
    setIsVisibilityFilterPositioned(true);
    setIsVisibilityFilterOpen(true);
  }, []);

  // Close visibility filter dropdown when clicking outside
  useEffect(() => {
    if (!isVisibilityFilterOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        visibilityFilterButtonRef.current && !visibilityFilterButtonRef.current.contains(target) &&
        visibilityFilterDropdownRef.current && !visibilityFilterDropdownRef.current.contains(target)
      ) {
        setIsVisibilityFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisibilityFilterOpen]);

  // Check if a column has active filters (cannot be hidden)
  const columnHasActiveFilter = useCallback((columnKey: string): boolean => {
    const filterParams = COLUMN_FILTER_PARAMS[columnKey];
    if (!filterParams) return false;
    return filterParams.some(param => {
      const value = params[param as keyof typeof params];
      return value !== undefined && value !== '';
    });
  }, [params]);

  const handleToggleColumnVisibility = useCallback((columnKey: string) => {
    if (columnHasActiveFilter(columnKey)) return;
    markFilterChange();
    setHiddenColumns(prev => {
      const next = prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey];
      localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(next));
      return next;
    });
  }, [columnHasActiveFilter, markFilterChange]);

  // Fetch total venue count (unfiltered) for the title
  const { data: totalVenueCount } = useQuery({
    queryKey: ['venues-total-count'],
    queryFn: async () => {
      const response = await venuesAPI.list({ page_size: 1 });
      return response.data.total;
    },
    staleTime: 60000,
  });

  // Fetch distinct address values for filtering
  const { data: addressFilterValues = [] } = useQuery({
    queryKey: ['venue-address-filter-values'],
    queryFn: async () => {
      const response = await venuesAPI.getAddressFilterValues();
      return response.data;
    },
    staleTime: 60000,
  });

  // Fetch distinct style values for filtering
  const { data: styleFilterValues = [] } = useQuery({
    queryKey: ['venue-style-filter-values'],
    queryFn: async () => {
      const response = await venuesAPI.getStyleFilterValues();
      return response.data;
    },
    staleTime: 60000,
  });

  // Fetch distinct source values for filtering
  const { data: sourceFilterValues = [] } = useQuery({
    queryKey: ['venue-source-filter-values'],
    queryFn: async () => {
      const response = await venuesAPI.getSourceFilterValues();
      return response.data;
    },
    staleTime: 60000,
  });

  // Build source ID -> filename lookup map
  const sourceFilenameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const sv of sourceFilterValues) {
      map[sv.value] = sv.label;
    }
    return map;
  }, [sourceFilterValues]);

  // Fetch user's bands for favorites column
  const { data: bands = [] } = useQuery({
    queryKey: ['bands'],
    queryFn: async () => {
      const response = await bandsAPI.list();
      return response.data.items;
    },
    staleTime: 60000,
  });

  const isLoading = isLoadingVenues;

  // Transform venues to table rows — contacts are embedded per-page by the backend
  const venueRows: VenueTableRow[] = useMemo(() => {
    return venues.map((venue) => {
      const venueContacts: SlimContact[] = venue.resolved_contacts || [];
      const sentCount = venue.sent_count ?? 0;
      const receivedCount = venue.received_count ?? 0;
      const lastContactDate = venue.last_contact_date;
      const lastContactSource = venue.last_contact_source;
      const hasEmailContact = venueContacts.some((c) => c.email);
      const hasPhoneContact = venueContacts.some((c) => c.phone);
      const hasAnyContact = venueContacts.length > 0;

      const flags: string[] = [];
      const overallInterest = (venue.interests || []).find(i => i.band_id === null);
      if (overallInterest) {
        if (overallInterest.level === 'very_interested' || overallInterest.level === 'concert_proposal') flags.push('Interested');
        if (overallInterest.level === 'not_interested') flags.push('Not Interested');
      }

      return {
        ...venue,
        sentCount,
        receivedCount,
        lastContactDate,
        lastContactSource,
        recontactAfter: venue.recontact?.after,
        flags,
        hasEmailContact,
        hasPhoneContact,
        hasAnyContact,
        venueContacts,
      };
    });
  }, [venues]);

  // Refs so handleOpenContact stays stable while always reading current venueRows/pagination
  const venueRowsRef = useRef(venueRows);
  venueRowsRef.current = venueRows;
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const handleOpenContact = useCallback(async (venue: Venue, contactId?: string) => {
    const token = openRequest.begin();
    setSelectedVenue(venue);
    setSelectedContactId(contactId);
    setSelectedVenueContacts([]);
    setIsContactOverlayOpen(true);
    const idxInPage = venueRowsRef.current.findIndex(r => r._id === venue._id);
    // A venue that is not on the page in hand (opened from a concert, say) has no position to
    // navigate from: keeping the previous one would send prev/next off to an unrelated row.
    setOverlayAbsoluteIndex(
      idxInPage !== -1 && paginationRef.current
        ? (paginationRef.current.page - 1) * paginationRef.current.pageSize + idxInPage
        : null,
    );
    if (venue.contact_ids.length > 0) {
      try {
        const res = await contactsAPI.batchGet(venue.contact_ids);
        // Contacts carry their mail bodies, so this can run for seconds. Closed since, or moved
        // on to another venue? Then these are not the contacts of what is on screen.
        if (!openRequest.isCurrent(token)) return;
        setSelectedVenueContacts(res.data);
      } catch (err) {
        console.error('Failed to fetch contacts for overlay:', err);
      }
    }
  }, [openRequest]);

  const handleOverlayNavigate = useCallback((direction: 'prev' | 'next') => {
    if (overlayAbsoluteIndex === null || isOverlayNavigating || !paginationRef.current) return;
    const p = paginationRef.current;
    const target = overlayAbsoluteIndex + (direction === 'next' ? 1 : -1);
    const targetPage = Math.floor(target / p.pageSize) + 1;
    const idxInPage = target % p.pageSize;

    if (targetPage === p.page) {
      const venue = venueRowsRef.current[idxInPage];
      if (venue) handleOpenContact(venue);
    } else {
      setIsOverlayNavigating(true);
      setPendingNavIndex(target);
      setPage(targetPage);
    }
  }, [overlayAbsoluteIndex, isOverlayNavigating, handleOpenContact, setPage]);

  // Resolve cross-page navigation once new page data has loaded.
  //
  // Every exit from here has to clear `pendingNavIndex` and `isOverlayNavigating`. A pending
  // index left behind freezes the overlay's prev/next — `handleOverlayNavigate` and both chevrons
  // are gated on `isNavigating` — and turns the next refetch into a navigation nobody asked for,
  // since this effect runs again on the new rows.
  useEffect(() => {
    if (pendingNavIndex === null) return;
    const giveUp = () => {
      setPendingNavIndex(null);
      setIsOverlayNavigating(false);
    };
    // Closed while the page was loading: nothing to navigate, and nothing to reopen.
    if (!isContactOverlayOpen) {
      giveUp();
      return;
    }
    if (isFetching || !pagination) return;
    const targetPage = Math.floor(pendingNavIndex / pagination.pageSize) + 1;
    // Still the previous page's rows. Nothing else can move the list while the overlay is open
    // — it covers the filter bar and the pager — so the target page is on its way.
    if (pagination.page !== targetPage) return;
    const venue = venueRows[pendingNavIndex % pagination.pageSize];
    // No row there: the result set shrank under the open overlay (contacts deleted out of a
    // contact-filtered list, venues hidden). Stay where we are rather than wait for a row that
    // is not coming.
    if (venue) handleOpenContact(venue);
    giveUp();
  }, [venueRows, pendingNavIndex, isFetching, pagination, handleOpenContact, isContactOverlayOpen]);

  const handleVenueSubmit = (venueData: VenueFormData): void => {
    if (venueModalMode === 'edit' && editingVenue) {
      const id = editingVenue._id;
      run(
        () => venuesAPI.update(id, venueData).then((res) => {
          // Keep the open contact overlay in sync with the edited venue
          setSelectedVenue(prev => (prev && prev._id === id ? res.data : prev));
          return refetchVenues();
        }),
        { loading: 'Updating venue...', success: 'Venue updated' },
      );
    } else {
      run(
        () => venuesAPI.create(venueData).then(() => refetchVenues()),
        { loading: 'Creating venue...', success: 'Venue created' },
      );
    }
  };

  const handleEditVenue = (venue: Venue) => {
    setEditingVenue(venue);
    setVenueModalMode('edit');
    setIsVenueModalOpen(true);
  };

  const handleDeleteVenue = (id: string): void => {
    run(
      () => venuesAPI.delete(id).then(() => refetchVenues()),
      { loading: 'Deleting venue...', success: 'Venue deleted' },
    );
  };

  const handleCloseVenueModal = () => {
    setIsVenueModalOpen(false);
    setEditingVenue(null);
    setVenueModalMode('create');
  };

  const handleOpenTodo = (venue: Venue) => {
    setTodoVenue(venue);
    setIsTodoModalOpen(true);
  };

  const handleCreateTodo = (data: TodoFormData): void => {
    if (!todoVenue) return;
    const venueId = todoVenue._id;
    setIsTodoModalOpen(false);
    setTodoVenue(null);
    run(
      () => venuesAPI.addTodo(venueId, data).then(() =>
        queryClient.invalidateQueries({ queryKey: ['todos'] })
      ),
      { loading: 'Creating todo...', success: 'Todo created' },
    );
  };

  const handleOpenConcert = (venue: Venue) => {
    setConcertVenue(venue);
    setIsConcertModalOpen(true);
  };

  const handleCreateConcert = (data: ConcertFormData): void => {
    setIsConcertModalOpen(false);
    setConcertVenue(null);
    run(
      () => concertsAPI.create({
        date: new Date(data.date).toISOString(),
        band_id: data.band_id,
        venue_id: data.venue_id,
        status: data.status,
        total_amount: data.total_amount || undefined,
        benefits: data.benefits || undefined,
        times: data.times || undefined,
        duration: data.duration || undefined,
        backline: data.backline || undefined,
        technique: data.technique || undefined,
        other_info: data.other_info || undefined,
        admin_contact: data.admin_contact || undefined,
        checklist: data.checklist.length > 0 ? data.checklist : undefined,
      }).then(() => queryClient.invalidateQueries({ queryKey: ['concerts'] })),
      { loading: 'Creating concert...', success: 'Concert created' },
    );
  };

  const handleToggleFavorite = useCallback(async (venueId: string, bandId: string) => {
    const queryKey = ['venues', params];
    const previousData = queryClient.getQueryData(queryKey);

    // Optimistic update — toggle favorite in cache immediately
    queryClient.setQueryData(queryKey, (old: PaginatedResponse<Venue> | undefined) => {
      if (!old) return old;
      return {
        ...old,
        items: old.items.map((v: Venue) => {
          if (v._id !== venueId) return v;
          const favIds = v.favorite_band_ids || [];
          const newFavIds = favIds.includes(bandId)
            ? favIds.filter((id: string) => id !== bandId)
            : [...favIds, bandId];
          return { ...v, favorite_band_ids: newFavIds };
        }),
      };
    });

    try {
      await venuesAPI.toggleFavorite(venueId, bandId);
    } catch (error) {
      queryClient.setQueryData(queryKey, previousData);
      console.error('Failed to toggle favorite:', error);
    }
  }, [params, queryClient]);

  const handleToggleQuickEdit = () => {
    const next = !isQuickEditEnabled;
    setIsQuickEditEnabled(next);
    localStorage.setItem('venues-quick-edit', String(next));
  };

  const handleToggleCompactMode = () => {
    const next = !isCompactMode;
    setIsCompactMode(next);
    localStorage.setItem('venues-compact-mode', String(next));
  };

  // Multi-select handlers
  const handleToggleMultiMode = useCallback(() => {
    setIsMultiMode((prev) => {
      if (prev) {
        setSelectedIds(new Set());
        setAllPagesSelected(false);
      }
      return !prev;
    });
  }, []);

  // Reset selection when filters/page change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    setSelectedIds(new Set());
    setAllPagesSelected(false);
  // paramsKey is a stable serialization of params — safe to use as dep
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // Escape key exits multi mode
  useEffect(() => {
    if (!isMultiMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMultiMode(false);
        setSelectedIds(new Set());
        setAllPagesSelected(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isMultiMode]);

  const handleToggleRow = useCallback((id: string) => {
    setAllPagesSelected(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleTogglePage = useCallback((pageIds: string[], allSelected: boolean) => {
    setAllPagesSelected(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const handleSelectAllPages = useCallback(() => {
    setAllPagesSelected(true);
    setSelectedIds(new Set());
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setAllPagesSelected(false);
  }, []);

  const selectedCount = allPagesSelected ? pagination?.total ?? 0 : selectedIds.size;

  /**
   * Describe the current selection for a bulk action.
   *
   * With select-all-pages on we hand the API the filter itself rather than a list of ids:
   * the server then resolves it over the whole result set, so the action covers every
   * matching venue instead of the ones that fitted in a page.
   *
   * It goes through `toApiParams`: `paused: 'all'` is a client-side sentinel for "no pause
   * filter", and the server's `paused` is a plain boolean that would reject it.
   */
  const selectionFilter = useCallback((): VenueFilter => {
    const { q, type, interest_level, interest_band, style, contact_filter,
      address_filter, ideal_contact_period, concert_period, sent_count, received_count,
      favorites, status, visibility, last_contact_after, last_contact_before,
      last_contact_never, source, flagged, paused } = params;
    return toApiParams({
      q, type, interest_level, interest_band, style, contact_filter,
      address_filter, ideal_contact_period, concert_period, sent_count, received_count,
      favorites, status, visibility, last_contact_after, last_contact_before,
      last_contact_never, source, flagged, paused,
    });
  }, [params]);

  /**
   * How many venues the current filters match but the pause hides.
   *
   * The safety rail for hiding rows by default: a venue that is not where the user expects reads
   * as *lost* rather than filtered. It feeds the empty state only — the toolbar chip is gone — so
   * the worst case (typing a venue's name and getting nothing back) still explains itself. Kept
   * under the ['venues'] prefix so a bulk pause refreshes it along with the lists.
   * See docs/VENUE_PAUSE.md.
   */
  const hiddenFilter = useMemo(
    () => ({ ...selectionFilter(), paused: 'true' as const, page_size: 1 }),
    [selectionFilter],
  );
  const { data: hiddenCount = 0 } = useQuery({
    queryKey: ['venues', 'hidden-count', hiddenFilter],
    queryFn: async () => (await venuesAPI.list(toApiParams(hiddenFilter))).data.total,
    enabled: (params.paused ?? 'false') === 'false',
    staleTime: 30000,
  });

  // Flag or unflag the selection. Like the other bulk actions, "select all" sends the filter so
  // the whole result set is covered. The lists are refetched afterwards: with a flag filter on,
  // the rows just (un)flagged leave the table, so the selection is cleared as for a delete.
  const handleBatchFlag = (flagged: boolean) => {
    const selection = allPagesSelected ? { filter: selectionFilter() } : { ids: [...selectedIds] };
    const label = `${selectedCount} ${selectedCount === 1 ? 'venue' : 'venues'}`;
    run(
      () => venuesAPI.batchFlag(selection, flagged).then(() => {
        handleClearSelection();
        return refreshCachedVenues(queryClient);
      }),
      flagged
        ? { loading: `Flagging ${label}...`, success: `${label} flagged` }
        : { loading: `Unflagging ${label}...`, success: `${label} unflagged` },
    );
  };

  // Pause the selection until a date, or lift its pause. Same shape as the bulk flag: with
  // "select all" on, the filter goes to the server so the whole result set is covered. Refetched
  // rather than patched — under the default "not hidden" view the rows just paused leave the
  // table, as they do after a delete. See docs/VENUE_PAUSE.md.
  const handleBatchRecontact = (recontact: RecontactRequest | null) => {
    const selection = allPagesSelected ? { filter: selectionFilter() } : { ids: [...selectedIds] };
    const label = `${selectedCount} ${selectedCount === 1 ? 'venue' : 'venues'}`;
    run(
      () => venuesAPI.batchRecontact(selection, recontact).then(() => {
        handleClearSelection();
        return refreshCachedVenues(queryClient);
      }),
      recontact
        ? { loading: `Pausing ${label}...`, success: `${label} paused` }
        : { loading: `Lifting the pause on ${label}...`, success: `Pause lifted on ${label}` },
    );
  };

  // One venue, from its row context menu: the same endpoint with a single id, so the interest and
  // the date stay in step server-side exactly as they do in the overlay.
  const handleRowRecontact = (row: VenueTableRow, recontact: RecontactRequest | null) => {
    run(
      () => venuesAPI.batchRecontact({ ids: [row._id] }, recontact).then(() => refreshCachedVenues(queryClient)),
      recontact
        ? { loading: 'Pausing venue...', success: 'Venue paused' }
        : { loading: 'Lifting the pause...', success: 'Pause lifted' },
    );
  };

  // Flag or unflag one venue from its row context menu. Optimistic like the overlay toggle
  // (docs/VENUE_FLAGS.md): the cached lists are patched, not refetched, so a row does not jump
  // out from under the pointer, and a failed save puts the old value back.
  const rowFlagSavesRef = useRef(new Map<string, Promise<unknown>>());
  const handleToggleRowFlag = (row: VenueTableRow) => {
    const venueId = row._id;
    const flagged = !row.flagged;
    patchCachedVenueFlags(queryClient, [venueId], flagged);
    toast.loading(flagged ? 'Flagging venue...' : 'Removing flag...', { id: ROW_FLAG_TOAST_ID });

    const saves = rowFlagSavesRef.current;
    const save = (saves.get(venueId) ?? Promise.resolve())
      .catch(() => {})
      .then(() => venuesAPI.setFlag(venueId, flagged));
    saves.set(venueId, save);
    save.then(
      () => toast.success(flagged ? 'Venue flagged' : 'Flag removed', { id: ROW_FLAG_TOAST_ID, duration: 1500 }),
      () => {
        // A later toggle already set the shown value; only the latest save may undo it
        if (saves.get(venueId) === save) patchCachedVenueFlags(queryClient, [venueId], !flagged);
        toast.error('Failed to update the flag', { id: ROW_FLAG_TOAST_ID });
      },
    ).finally(() => {
      if (saves.get(venueId) === save) saves.delete(venueId);
    });
  };

  const handleResetFlags = async () => {
    setIsResettingFlags(true);
    try {
      const { data } = await venuesAPI.resetFlags();
      await refreshCachedVenues(queryClient);
      setIsResetFlagsConfirmOpen(false);
      toast.success(data.count === 0 ? 'No venue was flagged' : `${data.count} ${data.count === 1 ? 'flag' : 'flags'} reset`);
    } catch (err) {
      console.error('Reset flags failed:', err);
      toast.error('Failed to reset flags');
    } finally {
      setIsResettingFlags(false);
    }
  };

  const handleBatchDelete = async () => {
    setIsBatchDeleting(true);
    try {
      await venuesAPI.batchDelete(
        allPagesSelected ? { filter: selectionFilter() } : { ids: [...selectedIds] }
      );
      await refetchVenues();
      setSelectedIds(new Set());
      setAllPagesSelected(false);
      setIsBatchDeleteConfirmOpen(false);
    } catch (err) {
      console.error('Batch delete failed:', err);
      toast.error('Failed to delete the selected venues');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  // --- Add selected venues' contacts to a mailing list ---
  // Resolved server-side: which contacts qualify, and which single one represents a venue,
  // are decided against every matching venue rather than a page of them.
  const resolveMailingListContacts = useCallback(
    async (flagOption: MailingListAddOptions['flagOption'], quantity: MailingListAddOptions['quantity']): Promise<MailingListContact[]> => {
      const selection = allPagesSelected
        ? { filter: selectionFilter() }
        : { venue_ids: [...selectedIds] };
      const response = await venuesAPI.mailingListContacts(selection, {
        flag_option: flagOption,
        quantity,
      });
      return response.data;
    },
    [allPagesSelected, selectionFilter, selectedIds]
  );

  const handleMlCleanup = useCallback(() => {
    setMlStep('closed');
    setMlContacts([]);
    setMlResolving(false);
    setMlSubmitting(false);
    setSelectedIds(new Set());
    setAllPagesSelected(false);
    queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
  }, [queryClient]);

  const handleMlConfigSubmit = async (opts: MailingListAddOptions) => {
    setMlResolving(true);
    try {
      const contacts = await resolveMailingListContacts(opts.flagOption, opts.quantity);
      if (contacts.length === 0) {
        toast.error('No matching contacts in the selected venues');
        return;
      }
      setMlContacts(contacts);
      setMlStep(opts.destination === 'new' ? 'create' : 'existing');
    } catch (err) {
      console.error('Failed to collect contacts:', err);
      toast.error('Failed to collect contacts');
    } finally {
      setMlResolving(false);
    }
  };

  const handleMlCreated = (saved?: MailingList) => {
    const n = mlContacts.length;
    toast.success(`Added ${n} contact${n !== 1 ? 's' : ''} to "${saved?.name ?? 'list'}"`);
    handleMlCleanup();
  };

  const handleMlPickExisting = async (list: MailingList) => {
    setMlSubmitting(true);
    try {
      const merged = [...list.contacts];
      const seen = new Set(list.contacts.map((c) => c.contact_id));
      let added = 0;
      for (const c of mlContacts) {
        if (seen.has(c.contact_id)) continue;
        seen.add(c.contact_id);
        merged.push(c);
        added++;
      }
      await mailingListsAPI.update(list._id, { contacts: merged });
      toast.success(`Added ${added} contact${added !== 1 ? 's' : ''} to "${list.name}"`);
      handleMlCleanup();
    } catch (err) {
      console.error('Failed to add contacts to list:', err);
      toast.error('Failed to add contacts to the list');
      setMlSubmitting(false);
    }
  };

  const selectionConfig: SelectionConfig<VenueTableRow> = useMemo(() => ({
    getRowId: (row) => row._id,
    selectedIds,
    onToggleRow: handleToggleRow,
    onTogglePage: handleTogglePage,
    allPagesSelected,
  }), [selectedIds, handleToggleRow, handleTogglePage, allPagesSelected]);

  const handleQuickEditSave = useCallback((venueId: string, data: Partial<VenueFormData>): void => {
    run(
      () => venuesAPI.update(venueId, data).then(() => refetchVenues()),
      { loading: 'Updating venue...', success: 'Venue updated' },
    );
  }, [refetchVenues, run]);

  const handleCellRightClick = useCallback((row: Venue, columnKey: string, position: { x: number; y: number }) => {
    setQuickEditState({ venue: row, field: columnKey, position });
  }, []);

  // Where each band sits in the user's own band order (Profile -> Bands). The interest chips are
  // read off the venue's stored array, whose order is the order the interests happened to be
  // written in, so they need this to line up with the favourites column (docs/BAND_ORDER.md).
  const bandOrder = useMemo(
    () => new Map(bands.map((band, index) => [band._id, index])),
    [bands],
  );

  // Build favorites filter values from bands
  const favoritesFilterValues = useMemo(() => [
    { value: 'none', label: 'No Favorites' },
    ...bands.map((band) => ({ value: band._id, label: band.name })),
  ], [bands]);

  const visibleColumns = useMemo(() => {
    /**
     * The commentary marker beside a venue name, shown only while the Commentary column
     * itself is hidden — otherwise the text is already on the row. Both the normal and the
     * compact name cell use it, so compact mode is not left without the note.
     */
    const commentaryIcon = (row: VenueTableRow) =>
      row.commentary && hiddenColumns.includes('commentary') ? (
        <Tooltip content={row.commentary} className="inline-flex flex-shrink-0 align-middle">
          <Info className="w-3.5 h-3.5 text-blue-400/60" />
        </Tooltip>
      ) : null;

    // A venue nobody has opened yet also carries a dot before its name — the same "unread" mark
    // as the bold name, in the form a list is scanned by (docs/VENUE_SEEN.md). It sits first in
    // the row, ahead of the public globe, so the dots line up down the column.
    const unseenDot = (row: VenueTableRow) =>
      row.seen ? null : (
        <span
          className="w-1.5 h-1.5 rounded-full bg-dark-accent flex-shrink-0"
          title="Not opened yet"
        />
      );

    const columns: Column<VenueTableRow>[] = [
    {
      key: 'favorites',
      label: '',
      labelRender: () => (
        <div className="flex justify-center" title="Favorites">
          <Star className="w-4 h-4 text-dark-text-muted" />
        </div>
      ),
      filterable: true,
      filterConfig: {
        values: favoritesFilterValues,
        selectedValues: params.favorites ? params.favorites.split(',') : [],
        onChange: (selected) => setFilter('favorites', selected.length > 0 ? selected.join(',') : undefined),
      },
      width: '120px',
      render: (row) => {
        const favoriteBandIds = row.favorite_band_ids || [];
        if (bands.length === 0) {
          return <span className="text-dark-text-muted text-xs">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {bands.map((band) => {
              const isFavorited = favoriteBandIds.includes(band._id);
              return (
                <button
                  key={band._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFavorite(row._id, band._id);
                  }}
                  className={`px-2 py-0.5 rounded text-xs transition-colors ${
                    isFavorited
                      ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                      : 'bg-dark-bg-tertiary text-dark-text-muted hover:bg-dark-bg-secondary hover:text-dark-text-secondary'
                  }`}
                  title={band.name}
                >
                  {band.short_name || band.name}
                </button>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      sortable: false,
      filterable: true,
      filterConfig: {
        values: VENUE_TYPE_FILTER_VALUES,
        selectedValues: params.type ? params.type.split(',') : [],
        onChange: (selected) => setFilter('type', selected.length > 0 ? selected.join(',') : undefined),
      },
      width: '110px',
      render: (row) => {
        const Icon = VENUE_TYPE_ICONS[row.type] || HelpCircle;
        return (
          <span className="inline-flex items-center gap-1.5 text-xs text-dark-text-secondary">
            <Icon size={14} className="shrink-0" />
            {row.type?.replace('_', ' ') ?? '—'}
          </span>
        );
      },
    },
    {
      key: 'styles',
      label: 'Style',
      sortable: false,
      filterable: true,
      filterConfig: {
        values: styleFilterValues.map(s => ({ value: s, label: s })),
        selectedValues: params.style ? params.style.split(',') : [],
        onChange: (selected) => setFilter('style', selected.length > 0 ? selected.join(',') : undefined),
      },
      width: '110px',
      render: (row) => {
        if (!row.styles || row.styles.length === 0) {
          return <span className="text-dark-text-muted">-</span>;
        }
        return (
          <span className="text-xs text-dark-text-secondary">
            {row.styles.join(', ')}
          </span>
        );
      },
    },
    {
      key: 'url',
      label: 'URL',
      sortable: false,
      width: '100px',
      render: (row) => {
        const href = externalUrl(row.url);
        if (!href) return <span className="text-dark-text-muted">-</span>;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary-400 hover:text-primary-300 truncate block max-w-[90px]"
            title={row.url}
            onClick={(e) => e.stopPropagation()}
          >
            {displayUrl(row.url)}
          </a>
        );
      },
    },
    {
      key: 'name',
      label: 'Venue Name',
      sortable: true,
      filterable: true,
      renderFilter: () => (
        <ColumnFilter
          values={VENUE_STATUS_FILTER_VALUES}
          selectedValues={params.status ? params.status.split(',') : []}
          onChange={(selected) => setFilter('status', selected.length > 0 ? selected.join(',') : undefined)}
        />
      ),
      width: '180px',
      compactRender: (row) => {
        const inactive = row.status === 'closed' || row.status === 'no_concerts';
        const statusLabel = row.status === 'closed' ? ' (Closed)' : row.status === 'no_concerts' ? ' (No Concerts)' : '';
        return (
          // Truncate the name rather than the row: the cell already clips at its width, and a
          // trailing marker would be the first thing cut off on exactly the long names that
          // tend to carry a note. `title` carries the full name, which the clipped row can no
          // longer hand to CompactCell's overflow check.
          <span className="flex items-center gap-1.5 min-w-0">
            {unseenDot(row)}
            {row.visibility === 'public' && <Globe className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />}
            <span
              className={clsx(
                'truncate',
                // A venue whose contact overlay was never opened is bold (docs/VENUE_SEEN.md)
                row.seen ? 'font-medium' : 'font-bold',
                inactive && 'line-through',
                // A flagged venue's name is red, the flag's own color (docs/VENUE_FLAGS.md)
                row.flagged ? 'text-red-400' : inactive && 'text-dark-text-muted',
              )}
              title={`${row.name}${statusLabel}`}
            >
              {row.name}{statusLabel}
            </span>
            {commentaryIcon(row)}
          </span>
        );
      },
      render: (row) => {
        const inactive = row.status === 'closed' || row.status === 'no_concerts';
        return (
          <div>
            <div className="flex items-center gap-1.5">
              {unseenDot(row)}
              {row.visibility === 'public' && (
                <Globe className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
              )}
              <span
                className={clsx(
                  // Bold until the venue's contact overlay has been opened (docs/VENUE_SEEN.md)
                  row.seen ? 'font-medium' : 'font-bold',
                  inactive && 'line-through',
                  row.flagged ? 'text-red-400' : inactive && 'text-dark-text-muted',
                )}
              >
                {row.name}
              </span>
              {commentaryIcon(row)}
              {row.visibility === 'pending_public' && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-900/30 text-amber-400 border border-amber-700/50 rounded-sm">
                  Pending
                </span>
              )}
            </div>
            {inactive && (
              <div className="text-xs text-dark-text-muted">
                {row.status === 'closed' ? 'Closed' : 'No Concerts'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'commentary',
      label: 'Commentary',
      sortable: false,
      width: '200px',
      render: (row) => {
        if (!row.commentary) return <span className="text-dark-text-muted">-</span>;
        return (
          <span className="text-xs text-dark-text-secondary">
            {row.commentary}
          </span>
        );
      },
    },
    {
      key: 'address',
      label: 'Address',
      sortable: false,
      filterable: true,
      renderFilter: () => (
        <AddressColumnFilter
          values={addressFilterValues}
          selectedValues={params.address_filter ? params.address_filter.split(',') : []}
          onChange={(selected) => setFilter('address_filter', selected.length > 0 ? selected.join(',') : undefined)}
        />
      ),
      width: '160px',
      compactRender: (row) => {
        const address = row.address as Address | undefined;
        if (!address) return <span className="text-dark-text-muted">-</span>;
        const parts = [address.city, address.administrative_region_1, address.administrative_region_2, address.country].filter(Boolean);
        const text = parts.length > 0 ? parts.join(', ') : address.unparsed_address;
        if (!text) return <span className="text-dark-text-muted">-</span>;
        return <span className="text-dark-text-secondary">{text}</span>;
      },
      render: (row) => {
        const address = row.address as Address | undefined;
        if (!address) return <span className="text-dark-text-muted">-</span>;

        const hasStructured = address.city || address.administrative_region_1 || address.administrative_region_2 || address.country;

        if (!hasStructured && address.unparsed_address) {
          return <div className="text-xs text-dark-text-secondary leading-tight">{address.unparsed_address}</div>;
        }

        if (!hasStructured) {
          return <span className="text-dark-text-muted">-</span>;
        }

        return (
          <div className="flex flex-col text-xs leading-tight">
            {address.city && <span className="text-dark-text-primary">{address.city}</span>}
            {address.administrative_region_1 && <span className="text-dark-text-secondary">{address.administrative_region_1}</span>}
            {address.administrative_region_2 && <span className="text-dark-text-muted">{address.administrative_region_2}</span>}
            {address.country && <span className="text-dark-text-muted">{address.country}</span>}
          </div>
        );
      },
    },
    {
      key: 'concert_period',
      label: 'Concert Period',
      sortable: false,
      filterable: true,
      filterConfig: {
        values: ALL_MONTHS_FILTER_VALUES,
        selectedValues: params.concert_period ? params.concert_period.split(',') : [],
        onChange: (selected) => setFilter('concert_period', selected.length > 0 ? selected.join(',') : undefined),
      },
      width: '100px',
      render: (row) => {
        const text = formatMonthPeriod(row.concert_period);
        if (text === '-') {
          return <span className="text-dark-text-muted">-</span>;
        }
        return <span className="text-xs text-dark-text-secondary">{text}</span>;
      },
    },
    {
      key: 'sent_count',
      label: '',
      labelRender: () => (
        <Tooltip
          content={
            <div className="space-y-1.5">
              <div>
                Communications <span className="text-white">sent</span>, counted over the same
                contacts as Last Contact — this venue's active ones, else its gatekeepers, else
                whoever is left.
              </div>
              <div>So a venue nobody reachable has been written to reads 0, as it reads Never.</div>
            </div>
          }
        >
          <div className="flex justify-center">
            <ArrowUpRight className="w-4 h-4 text-dark-text-muted" />
          </div>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterConfig: {
        values: COMM_COUNT_FILTER_VALUES,
        selectedValues: params.sent_count ? params.sent_count.split(',') : [],
        onChange: (selected) => setFilter('sent_count', selected.length > 0 ? selected.join(',') : undefined),
      },
      width: '50px',
      render: (row) => <div className="text-center">{row.sentCount}</div>,
    },
    {
      key: 'received_count',
      label: '',
      labelRender: () => (
        <Tooltip
          content={
            <div className="space-y-1.5">
              <div>
                Communications <span className="text-white">received</span>, counted over the same
                contacts as Last Contact — this venue's active ones, else its gatekeepers, else
                whoever is left.
              </div>
              <div>So a venue nobody reachable has been written to reads 0, as it reads Never.</div>
            </div>
          }
        >
          <div className="flex justify-center">
            <ArrowDownLeft className="w-4 h-4 text-dark-text-muted" />
          </div>
        </Tooltip>
      ),
      filterable: true,
      filterConfig: {
        values: COMM_COUNT_FILTER_VALUES,
        selectedValues: params.received_count ? params.received_count.split(',') : [],
        onChange: (selected) => setFilter('received_count', selected.length > 0 ? selected.join(',') : undefined),
      },
      width: '50px',
      render: (row) => <div className="text-center">{row.receivedCount}</div>,
    },
    {
      key: 'last_contact_date',
      label: 'Last Contact',
      labelRender: () => (
        <Tooltip
          content={
            <div className="space-y-1.5">
              <div>
                The last time this venue's <span className="text-white">active</span> contacts were
                reached. 
              </div>
              <div>
                A venue with no active contact falls back to its gatekeepers, then to whoever is
                left (expired, do not contact).
              </div>
            </div>
          }
        >
          <span className="border-b border-dotted border-dark-text-muted">Last Contact</span>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      renderFilter: () => (
        <DateRangeFilter
          fromDate={params.last_contact_after}
          toDate={params.last_contact_before}
          onFromChange={(val) => setFilter('last_contact_after', val)}
          onToChange={(val) => setFilter('last_contact_before', val)}
          neverChecked={params.last_contact_never === 'true'}
          onNeverChange={(checked) => setFilter('last_contact_never', checked ? 'true' : undefined)}
          neverLabel="Never contacted"
          onClear={() => setFilters({
            last_contact_after: undefined,
            last_contact_before: undefined,
            last_contact_never: undefined,
          })}
        />
      ),
      width: '120px',
      render: (row) => {
        if (!row.lastContactDate) return 'Never';
        const note = row.lastContactSource ? LAST_CONTACT_SOURCE_NOTE[row.lastContactSource] : undefined;
        return (
          <Tooltip
            content={
              <div>
                <div>{recencyLabel(row.lastContactDate)}</div>
                {note && <div className="text-dark-text-muted">{note}</div>}
              </div>
            }
          >
            <span style={{ color: recencyColor(row.lastContactDate) }}>
              {format(new Date(row.lastContactDate), 'MMM d, yyyy')}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'interests',
      label: 'Interest',
      filterable: true,
      renderFilter: () => (
        <InterestColumnFilter
          bands={bands}
          selectedLevels={params.interest_level ? params.interest_level.split(',') : []}
          selectedBand={params.interest_band}
          onChangeLevel={(levels) => setFilter('interest_level', levels.length > 0 ? levels.join(',') : undefined)}
          onChangeBand={(band) => setFilter('interest_band', band)}
          onChangeBoth={(band, levels) => setFilters({ interest_band: band, interest_level: levels.length > 0 ? levels.join(',') : undefined })}
        />
      ),
      width: '120px',
      render: (row) => {
        const overall = (row.interests || []).find((i: { band_id: string | null }) => i.band_id === null);
        const level = overall?.level || 'no_information';
        const config: Record<string, { variant: 'default' | 'error' | 'warning' | 'info' | 'success' | 'primary'; label: string }> = {
          no_information: { variant: 'default', label: 'No Info' },
          not_interested: { variant: 'error', label: 'Not Interested' },
          not_interested_for_now: { variant: 'warning', label: 'Not Now' },
          mildly_interested: { variant: 'info', label: 'Mildly' },
          very_interested: { variant: 'success', label: 'Very Interested' },
          concert_proposal: { variant: 'primary', label: 'Proposal' },
        };
        const { variant, label } = config[level] || config.no_information;
        const bandInterests = (row.interests || [])
          .filter(
            (i: { band_id: string | null; level: string }) => i.band_id !== null && i.level !== 'no_information'
          )
          // A band no longer on file sorts last rather than first.
          .sort((a, b) =>
            (bandOrder.get(a.band_id as string) ?? Number.MAX_SAFE_INTEGER) -
            (bandOrder.get(b.band_id as string) ?? Number.MAX_SAFE_INTEGER)
          );
        return (
          <div className="flex flex-wrap gap-1">
            {level !== 'no_information' && (
              // The pause rides on this badge rather than taking a line of its own: the cell is
              // dense, and the Recontact From column already shows the date (docs/VENUE_PAUSE.md).
              <Badge
                variant={variant}
                className="text-xs hover:scale-100!"
                title={isPaused(row) ? `${label} — do not recontact before ${format(recontactDate(row)!, 'MMM d, yyyy')}` : label}
              >
                All
              </Badge>
            )}
            {bandInterests.map((bi) => {
              const bandId = bi.band_id as string;
              const band = bands.find(b => b._id === bandId);
              const biConfig = config[bi.level] || config.no_information;
              return (
                <span key={bandId} title={`${band?.name || '?'}: ${biConfig.label}`}>
                  <Badge variant={biConfig.variant} className="text-xs hover:scale-100!">
                    {band?.short_name || '?'}
                  </Badge>
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'contacts',
      label: 'Contacts',
      filterable: true,
      renderFilter: () => (
        <ColumnFilter
          values={CONTACT_FILTER_VALUES}
          selectedValues={params.contact_filter ? params.contact_filter.split(',') : []}
          onChange={(selected) => setFilter('contact_filter', selected.length > 0 ? selected.join(',') : undefined)}
        />
      ),
      width: '160px',
      render: (row) => {
        const venueContacts = row.venueContacts;
        if (venueContacts.length === 0) {
          return null;
        }

        const getContactColor = (contact: SlimContact) => {
          const inactive = contact.flags === 'do_not_contact' || contact.flags === 'expired';
          const gatekeeper = contact.flags === 'gatekeeper';
          const base = gatekeeper ? 'text-dark-text-muted' : contact.email ? 'text-dark-text-primary' : contact.phone ? 'text-dark-text-secondary' : 'text-dark-text-muted';
          return inactive ? `${base} line-through` : base;
        };

        const getContactDisplayName = (contact: SlimContact) => {
          return contact.name || contact.email || contact.phone || 'Unknown';
        };

        const getContactTooltip = (contact: SlimContact) => {
          const parts: string[] = [];
          if (contact.email) parts.push(contact.email);
          if (contact.phone) parts.push(contact.phone);
          if (contact.flags && contact.flags !== 'none') parts.push(`[${contact.flags.replace('_', ' ')}]`);
          if (contact.commentary) parts.push(`"${contact.commentary}"`);
          return parts.join(' • ') || 'No contact info';
        };

        return (
          <div className="flex flex-wrap gap-1">
            {venueContacts.map((contact, idx) => (
              <span
                key={contact._id}
                className={`text-xs ${getContactColor(contact)} cursor-pointer hover:underline`}
                title={getContactTooltip(contact)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenContact(row, contact._id);
                }}
              >
                {getContactDisplayName(contact)}{contact.function ? ` (${contact.function})` : ''}
                {idx < venueContacts.length - 1 && ','}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'ideal_contact_period',
      label: 'Contact Period',
      sortable: false,
      filterable: true,
      filterConfig: {
        values: CONTACT_PERIOD_FILTER_VALUES,
        selectedValues: params.ideal_contact_period ? params.ideal_contact_period.split(',') : [],
        onChange: (selected) => setFilter('ideal_contact_period', selected.length > 0 ? selected.join(',') : undefined),
      },
      width: '120px',
      render: (row) => {
        const text = formatMonthPeriod(row.ideal_contact_period);
        if (text === '-') {
          return <span className="text-dark-text-muted">-</span>;
        }
        return <span className="text-xs text-dark-text-secondary">{text}</span>;
      },
    },
    {
      key: 'source',
      label: 'Source',
      sortable: false,
      filterable: true,
      filterConfig: {
        values: [
          { value: 'none', label: 'Manual' },
          ...sourceFilterValues,
        ],
        selectedValues: params.source ? params.source.split(',') : [],
        onChange: (selected) => setFilter('source', selected.length > 0 ? selected.join(',') : undefined),
      },
      width: '120px',
      render: (row) => {
        if (!row.source) {
          return <span className="text-dark-text-muted">-</span>;
        }
        const filename = sourceFilenameMap[row.source] || 'Import';
        return (
          <span className="text-xs text-dark-text-secondary truncate block max-w-[100px]" title={filename}>
            {filename}
          </span>
        );
      },
    },
    {
      // The pause, as a sortable queue: who becomes contactable first. The badge in the Interest
      // column is the indicator; this is for scanning and sorting. See docs/VENUE_PAUSE.md.
      key: 'recontact_after',
      label: 'Recontact From',
      labelRender: () => (
        <Tooltip
          content={
            <div className="space-y-1.5">
              <div>
                When this venue may be written to again — it asked to be left alone until then.
              </div>
              <div>
                Paused venues are hidden unless the hourglass says otherwise, and campaigns leave
                them out. A past date is greyed: the venue is contactable again.
              </div>
            </div>
          }
        >
          <span className="border-b border-dotted border-dark-text-muted">Recontact From</span>
        </Tooltip>
      ),
      // No header filter: the toolbar hourglass is the one place the `paused` filter lives, and the
      // column is hidden by default, so a second copy here would be unreachable half the time.
      sortable: true,
      width: '130px',
      render: (row) => {
        const after = recontactDate(row);
        if (!after) return <span className="text-dark-text-muted">-</span>;
        const paused = isPaused(row);
        return (
          <Tooltip
            content={
              <div>
                <div>{recencyLabel(after)}</div>
                {row.recontact?.reason && <div className="text-dark-text-muted">{row.recontact.reason}</div>}
                {!paused && <div className="text-dark-text-muted">The pause has run out.</div>}
              </div>
            }
          >
            {/* No recencyColor(): its ramp measures age and clamps future dates to green. */}
            <span className={paused ? 'text-orange-400' : 'text-dark-text-muted line-through'}>
              {format(after, 'MMM d, yyyy')}
            </span>
          </Tooltip>
        );
      },
    },
  ];

    return columns.filter(col => !hiddenColumns.includes(col.key));
  }, [hiddenColumns, params, bands, bandOrder, styleFilterValues, addressFilterValues, sourceFilterValues, sourceFilenameMap, setFilter, setFilters, handleToggleFavorite, handleOpenContact, favoritesFilterValues]);

  const getRowContextMenu = (row: VenueTableRow): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [
      {
        label: 'Edit Venue',
        icon: <Pencil className="w-4 h-4" />,
        onClick: () => handleEditVenue(row),
      },
      {
        label: 'Contact Venue',
        icon: <MessageSquare className="w-4 h-4" />,
        onClick: () => handleOpenContact(row),
      },
      {
        label: 'Add Todo',
        icon: <Bell className="w-4 h-4" />,
        onClick: () => handleOpenTodo(row),
      },
      {
        label: 'Create Concert',
        icon: <Music className="w-4 h-4" />,
        onClick: () => handleOpenConcert(row),
      },
      {
        label: isPaused(row) ? 'Change Pause…' : 'Pause Venue…',
        icon: <Hourglass className={clsx('w-4 h-4', isPaused(row) && 'text-orange-400')} />,
        onClick: () => setPauseModalRow(row),
      },
      ...(isPaused(row)
        ? [{
            label: 'Lift Pause',
            icon: <Hourglass className="w-4 h-4" />,
            onClick: () => handleRowRecontact(row, null),
          }]
        : []),
      {
        label: row.flagged ? 'Unflag Venue' : 'Flag Venue',
        icon: row.flagged
          ? <FlagOff className="w-4 h-4" />
          : <Flag className="w-4 h-4 text-red-500" fill="currentColor" />,
        onClick: () => handleToggleRowFlag(row),
      },
    ];

    const websiteUrl = externalUrl(row.url);
    if (websiteUrl) {
      items.push({
        label: 'Open Website',
        icon: <ExternalLink className="w-4 h-4" />,
        onClick: () => window.open(websiteUrl, '_blank'),
      });
    }

    if (row.visibility === 'public') {
      items.push({
        label: 'Report a Mistake',
        icon: <AlertTriangle className="w-4 h-4" />,
        onClick: () => {
          setReportVenue(row);
          setIsReportMistakeOpen(true);
        },
      });
    }

    return items;
  };

  // Build sort config for Table component
  const tableSortConfig = params.sort_by
    ? { key: params.sort_by, direction: (params.sort_order || 'desc') as 'asc' | 'desc' }
    : null;

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-2xl) mx-auto w-full p-3 sm:p-4 md:p-6 flex-1 flex flex-col">
        <div className="mb-6 animate-slideDown flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-dark-text-primary mb-2 tracking-tight flex items-center gap-2">
              <MapPin className="w-6 h-6" />
              Venues{totalVenueCount !== undefined && ` (${totalVenueCount})`}
            </h1>
            <p className="text-sm text-dark-text-secondary">
              Manage your venue contacts and communications
            </p>
          </div>
          <div className="flex items-center gap-2 animate-scaleIn">
            <Button
              variant="primary"
              onClick={() => {
                setVenueModalMode('create');
                setEditingVenue(null);
                setIsVenueModalOpen(true);
              }}
              size="sm"
            >
              <Plus className="w-4 h-4" />
              New Venue
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/import')}
              size="sm"
            >
              <Upload className="w-4 h-4" />
              Import
            </Button>
            <button
              onClick={handleToggleMultiMode}
              title={isMultiMode ? 'Exit multi-select mode' : 'Enable multi-select mode'}
              className={clsx(
                'px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors flex items-center gap-1.5',
                isMultiMode
                  ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                  : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
              )}
            >
              <CheckSquare className="w-4 h-4" />
              Multi
            </button>
            <button
              onClick={handleToggleQuickEdit}
              title={isQuickEditEnabled ? 'Disable quick edit (right-click)' : 'Enable quick edit (right-click)'}
              className={clsx(
                'p-2 rounded-lg border transition-colors',
                isQuickEditEnabled
                  ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                  : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
              )}
            >
              <MousePointerClick className="w-4 h-4" />
            </button>
            <button
              onClick={handleToggleCompactMode}
              title={isCompactMode ? 'Disable compact mode' : 'Enable compact mode (single-line rows)'}
              className={clsx(
                'p-2 rounded-lg border transition-colors',
                isCompactMode
                  ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                  : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
              )}
            >
              <Rows3 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 card animate-scaleIn flex flex-col min-h-0">
          {/* Venue Views Bar */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {(() => {
              const effectiveViewId = activeViewId || pendingViewId;
              const isAllActive = !effectiveViewId && !isUnsavedView;
              // Lit when it is what the user is looking at: either they clicked it, or they
              // filtered their way into exactly what it holds with no named view applied. One
              // chip at a time — under an applied view, that view is the one lit.
              const isLatestActive = !!latestView && (
                effectiveViewId === latestView._id ||
                (!effectiveViewId && filtersMatch(getCurrentFilters(), latestView.filters))
              );
              return (
                <>
                <button
                  onClick={handleResetToAll}
                  className={clsx(
                    'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors',
                    isAllActive
                      ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                      : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                  )}
                >
                  All
                </button>
            {namedViews.map((view) => (
              <div
                key={view._id}
                className={clsx(
                  'group relative flex items-center transition-all',
                  draggedViewId === view._id && 'opacity-40',
                )}
                draggable
                onDragStart={(e) => handleDragStart(e, view._id)}
                onDragOver={(e) => handleDragOver(e, view._id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, view._id)}
                onDragEnd={handleDragEnd}
              >
                {dropIndicator?.viewId === view._id && dropIndicator.side === 'left' && (
                  <div className="absolute left-0 top-0.5 bottom-0.5 w-0.5 bg-primary-400 rounded-full -translate-x-1.5 z-10" />
                )}
                <button
                  onClick={() => handleApplyView(view)}
                  className={clsx(
                    'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors',
                    (activeViewId === view._id || pendingViewId === view._id)
                      ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                      : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                  )}
                >
                  {view.name}
                </button>
                {dropIndicator?.viewId === view._id && dropIndicator.side === 'right' && (
                  <div className="absolute right-0 top-0.5 bottom-0.5 w-0.5 bg-primary-400 rounded-full translate-x-1.5 z-10" />
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingView(view);
                  }}
                  className="absolute -right-1 -top-1 hidden group-hover:flex items-center justify-center w-4 h-4 rounded-full bg-dark-elevated border border-dark-border text-dark-text-muted hover:text-primary-400 hover:border-primary-700/50 transition-colors"
                  title="Edit view"
                >
                  <Pencil className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
            {latestView && (
              <button
                onClick={() => handleApplyView(latestView)}
                title="The filters you last picked, saved automatically"
                className={clsx(
                  'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors flex items-center gap-1',
                  isLatestActive
                    ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                    : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                )}
              >
                <History className="w-3 h-3" />
                {latestView.name}
              </button>
            )}
            <button
              ref={saveViewButtonRef}
              onClick={() => setIsSaveViewModalOpen(true)}
              className={clsx(
                'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors flex items-center gap-1',
                isUnsavedView
                  ? 'bg-primary-900/30 text-primary-400 border border-dashed border-primary-700/50'
                  : 'bg-dark-elevated text-dark-text-muted border border-dashed border-dark-border hover:border-primary-700/50 hover:text-primary-400'
              )}
            >
              <Save className="w-3 h-3" />
              Save current view
            </button>
                </>
              );
            })()}
          </div>

          {/* Search and top pagination */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4 items-center">
            <SearchInput
              value={params.q || ''}
              onChange={setSearch}
              placeholder="Search venues..."
              className="flex-1 max-w-md"
            />

            {/* Public/Private visibility filter */}
            <button
              ref={visibilityFilterButtonRef}
              onClick={() => isVisibilityFilterOpen ? setIsVisibilityFilterOpen(false) : openVisibilityFilter()}
              title="Public/Private filter"
              className={clsx(
                'p-2 rounded-lg border transition-colors',
                isVisibilityFilterOpen || params.visibility
                  ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                  : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
              )}
            >
              <Globe className="w-4 h-4" />
            </button>
            {isVisibilityFilterOpen && createPortal(
              <div
                ref={visibilityFilterDropdownRef}
                className={clsx(
                  'fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl min-w-[180px] flex flex-col overflow-hidden',
                  isVisibilityFilterPositioned ? 'animate-scaleIn' : 'opacity-0 pointer-events-none'
                )}
                style={isVisibilityFilterPositioned
                  ? { top: visibilityFilterPosition.top, left: visibilityFilterPosition.left }
                  : { top: -9999, left: -9999 }
                }
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-1.5 text-xs font-medium text-dark-text-muted border-b border-dark-border">
                  Visibility
                </div>
                <div className="py-1">
                  {VENUE_VISIBILITY_FILTER_VALUES.map((item) => {
                    const selectedVisibility = params.visibility ? params.visibility.split(',') : [];
                    const isSelected = selectedVisibility.includes(item.value);
                    return (
                      <button
                        key={item.value}
                        onClick={() => {
                          const current = params.visibility ? params.visibility.split(',') : [];
                          const next = isSelected
                            ? current.filter((v) => v !== item.value)
                            : [...current, item.value];
                          setFilter('visibility', next.length > 0 ? next.join(',') : undefined);
                        }}
                        className={clsx(
                          'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                          isSelected
                            ? 'bg-primary-600/20 text-primary-300'
                            : 'text-dark-text-secondary hover:bg-dark-surface'
                        )}
                      >
                        <div
                          className={clsx(
                            'w-4 h-4 rounded-sm border flex items-center justify-center flex-shrink-0',
                            isSelected
                              ? 'bg-primary-500 border-primary-500'
                              : 'border-dark-border'
                          )}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
                {params.visibility && (
                  <div className="px-2 py-1.5 border-t border-dark-border">
                    <button
                      onClick={() => {
                        setFilter('visibility', undefined);
                        setIsVisibilityFilterOpen(false);
                      }}
                      className="text-xs text-dark-text-secondary hover:text-dark-text-primary transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>,
              document.body
            )}

            {/* Column visibility settings */}
            <button
              ref={columnSettingsButtonRef}
              onClick={() => isColumnSettingsOpen ? setIsColumnSettingsOpen(false) : openColumnSettings()}
              title="Column settings"
              className={clsx(
                'p-2 rounded-lg border transition-colors',
                isColumnSettingsOpen || hiddenColumns.length > 0
                  ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                  : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
              )}
            >
              <Settings className="w-4 h-4" />
            </button>
            {isColumnSettingsOpen && createPortal(
              <div
                ref={columnSettingsDropdownRef}
                className={clsx(
                  'fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl min-w-[220px] flex flex-col overflow-hidden',
                  isColumnSettingsPositioned ? 'animate-scaleIn' : 'opacity-0 pointer-events-none'
                )}
                style={isColumnSettingsPositioned
                  ? { top: columnSettingsPosition.top, left: columnSettingsPosition.left, maxHeight: `${window.innerHeight - columnSettingsPosition.top - 8}px` }
                  : { top: -9999, left: -9999 }
                }
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-1.5 text-xs font-medium text-dark-text-muted border-b border-dark-border">
                  Columns to display
                </div>
                <div className="flex-1 overflow-y-auto py-1">
                  {Object.entries(COLUMN_LABELS).map(([key, label]) => {
                    const hasFilter = columnHasActiveFilter(key);
                    const isVisible = !hiddenColumns.includes(key);
                    return (
                      <label
                        key={key}
                        className={clsx(
                          'flex items-center gap-2 px-3 py-1.5 text-sm transition-colors',
                          hasFilter
                            ? 'text-dark-text-muted cursor-not-allowed'
                            : 'text-dark-text-secondary hover:bg-dark-surface cursor-pointer'
                        )}
                        title={hasFilter ? 'Cannot hide column with active filter' : undefined}
                        onClick={(e) => {
                          if (hasFilter) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          disabled={hasFilter}
                          onChange={() => handleToggleColumnVisibility(key)}
                          className="accent-primary-500 w-3.5 h-3.5"
                        />
                        <span>{label}</span>
                        {hasFilter && (
                          <span className="ml-auto text-[10px] text-amber-400">filtered</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>,
              document.body
            )}

            {/* Flag filter + reset */}
            <VenueFlagMenu
              value={params.flagged as FlagFilterValue}
              onChange={(value) => setFilter('flagged', value)}
              onReset={() => setIsResetFlagsConfirmOpen(true)}
            />

            {/* Paused venues: all / not hidden (default) / hidden — docs/VENUE_PAUSE.md */}
            <VenueHiddenMenu
              value={(params.paused ?? 'false') as HiddenFilterValue}
              onChange={(value) => setFilter('paused', value)}
            />

            {/* Top pagination controls */}
            {pagination && pagination.totalPages > 0 && (
              <div className="ml-auto">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  hasNext={pagination.hasNext}
                  hasPrev={pagination.hasPrev}
                  onPageChange={setPage}
                  isFetching={isFetching}
                  compact
                />
              </div>
            )}
          </div>

          {/* Multi-select action bar */}
          {isMultiMode && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2 bg-primary-900/20 border border-primary-700/40 rounded-lg text-sm">
              <span className={clsx('font-medium', selectedCount > 0 ? 'text-primary-300' : 'text-dark-text-muted')}>
                {selectedCount > 0
                  ? `${selectedCount} ${selectedCount === 1 ? 'venue' : 'venues'} selected`
                  : 'No venues selected'}
              </span>
              {/* Select-all-pages banner */}
              {!allPagesSelected && pagination && selectedIds.size === venueRows.length && venueRows.length > 0 && pagination.total > venueRows.length && (
                <button
                  onClick={handleSelectAllPages}
                  className="text-xs text-primary-400 hover:text-primary-300 underline underline-offset-2 transition-colors"
                >
                  Select all {pagination.total} venues matching current filters
                </button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <button
                  disabled={selectedCount === 0}
                  onClick={() => {/* Batch email — not implemented */}}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-xs font-medium',
                    selectedCount > 0
                      ? 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                      : 'border-dark-border bg-dark-elevated text-dark-text-muted opacity-40 cursor-not-allowed'
                  )}
                  title="Batch email (coming soon)"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Batch Email
                </button>
                <button
                  disabled={selectedCount === 0}
                  onClick={() => setMlStep('config')}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-xs font-medium',
                    selectedCount > 0
                      ? 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                      : 'border-dark-border bg-dark-elevated text-dark-text-muted opacity-40 cursor-not-allowed'
                  )}
                  title="Add selected venues' contacts to a mailing list"
                >
                  <MailPlus className="w-3.5 h-3.5" />
                  Add to Mailing List
                </button>
                {([true, false] as const).map((flagged) => (
                  <button
                    key={String(flagged)}
                    disabled={selectedCount === 0}
                    onClick={() => handleBatchFlag(flagged)}
                    className={clsx(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-xs font-medium',
                      selectedCount > 0
                        ? 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                        : 'border-dark-border bg-dark-elevated text-dark-text-muted opacity-40 cursor-not-allowed'
                    )}
                    title={flagged ? 'Flag the selected venues' : 'Unflag the selected venues'}
                  >
                    {flagged
                      ? <Flag className="w-3.5 h-3.5 text-red-500" fill="currentColor" />
                      : <FlagOff className="w-3.5 h-3.5" />}
                    {flagged ? 'Flag' : 'Unflag'}
                  </button>
                ))}
                <button
                  disabled={selectedCount === 0}
                  onClick={() => setIsPauseBulkOpen(true)}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-xs font-medium',
                    selectedCount > 0
                      ? 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                      : 'border-dark-border bg-dark-elevated text-dark-text-muted opacity-40 cursor-not-allowed'
                  )}
                  title="Pause the selected venues until a date, or lift their pause"
                >
                  <Hourglass className="w-3.5 h-3.5" />
                  Pause
                </button>
                <button
                  disabled={selectedCount === 0}
                  onClick={() => setIsBatchDeleteConfirmOpen(true)}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-xs font-medium',
                    selectedCount > 0
                      ? 'border-red-700/50 bg-red-900/20 text-red-400 hover:bg-red-900/40'
                      : 'border-dark-border bg-dark-elevated text-dark-text-muted opacity-40 cursor-not-allowed'
                  )}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
                {selectedCount > 0 && (
                  <button
                    onClick={handleClearSelection}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-dark-text-muted hover:text-dark-text-secondary transition-colors text-xs"
                    title="Clear selection"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Table */}
          <div className="flex-1 min-h-0 h-full relative">
            <TableLoadingOverlay isVisible={isFetching && !isLoading} />
            {isLoading ? (
              <TableSkeleton rows={8} />
            ) : (
              <Table
                data={venueRows}
                columns={visibleColumns}
                searchable={false}
                emptyMessage={
                  venuesError ? 'Failed to load venues'
                  : hiddenCount > 0 && (params.paused ?? 'false') === 'false' ? (
                    <span>
                      No venues found —{' '}
                      <button
                        onClick={() => setFilter('paused', 'all')}
                        className="text-primary-400 hover:text-primary-300 underline underline-offset-2"
                      >
                        {hiddenCount} paused {hiddenCount === 1 ? 'venue matches' : 'venues match'}. Show them.
                      </button>
                    </span>
                  )
                  : 'No venues found'
                }
                serverSort={true}
                sortConfig={tableSortConfig}
                onSort={setSort}
                rowClassName={(row) => clsx(
                  (row.status === 'closed' || row.status === 'no_concerts') && 'opacity-50',
                  isContactOverlayOpen && row._id === selectedVenue?._id && 'ring-1 ring-inset ring-primary-500 bg-primary-900/10',
                )}
                rowContextMenu={isMultiMode ? undefined : getRowContextMenu}
                onRowModClick={isMultiMode ? undefined : (row) => handleOpenContact(row)}
                onCellRightClick={isMultiMode ? undefined : (isQuickEditEnabled ? handleCellRightClick : undefined)}
                selection={isMultiMode ? selectionConfig : undefined}
                compact={isCompactMode}
              />
            )}
          </div>

          {/* Pagination */}
          {pagination && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              pageSize={pagination.pageSize}
              hasNext={pagination.hasNext}
              hasPrev={pagination.hasPrev}
              onPageChange={setPage}
              isFetching={isFetching}
              hideShowingText
            />
          )}
        </div>
      </div>

      <ContactOverlay
        isOpen={isContactOverlayOpen}
        onClose={() => {
          openRequest.cancel();
          setIsContactOverlayOpen(false);
          setSelectedVenue(null);
          setSelectedVenueContacts([]);
          setSelectedContactId(undefined);
          setOverlayAbsoluteIndex(null);
          setPendingNavIndex(null);
          setIsOverlayNavigating(false);
        }}
        venue={selectedVenue}
        contacts={selectedVenueContacts}
        bands={bands}
        initialContactId={selectedContactId}
        onRefreshContacts={() => {
          refetchVenues();
        }}
        onRefreshVenues={() => {
          refetchVenues();
        }}
        onEditVenue={handleEditVenue}
        hasPrev={overlayAbsoluteIndex !== null && overlayAbsoluteIndex > 0}
        hasNext={overlayAbsoluteIndex !== null && pagination != null && overlayAbsoluteIndex < pagination.total - 1}
        isNavigating={isOverlayNavigating}
        onNavigate={handleOverlayNavigate}
      />

      <VenueModal
        isOpen={isVenueModalOpen}
        onClose={handleCloseVenueModal}
        onSubmit={handleVenueSubmit}
        onDelete={handleDeleteVenue}
        venue={editingVenue}
        mode={venueModalMode}
        onSelectExistingVenue={handleEditVenue}
        readonlyCoreFields={
          venueModalMode === 'edit' && editingVenue != null && !user?.is_admin &&
          (editingVenue.visibility === 'public' || editingVenue.visibility === 'pending_public')
        }
      />

      <SaveViewModal
        isOpen={isSaveViewModalOpen}
        onClose={() => setIsSaveViewModalOpen(false)}
        onSubmit={handleSaveView}
        onUpdate={handleUpdateViewFilters}
        currentFilters={getCurrentFilters()}
        existingViews={namedViews}
      />

      <EditViewModal
        isOpen={!!editingView}
        onClose={() => setEditingView(null)}
        view={editingView}
        onRename={handleRenameView}
        onDelete={handleDeleteView}
      />

      <ReportMistakeModal
        isOpen={isReportMistakeOpen}
        onClose={() => {
          setIsReportMistakeOpen(false);
          setReportVenue(null);
        }}
        venue={reportVenue}
      />

      <TodoModal
        isOpen={isTodoModalOpen}
        onClose={() => {
          setIsTodoModalOpen(false);
          setTodoVenue(null);
        }}
        onSubmit={handleCreateTodo}
        venueName={todoVenue?.name}
        contacts={todoVenue?.resolved_contacts ?? []}
        bands={bands}
        optionalDescription
      />

      <ConcertModal
        isOpen={isConcertModalOpen}
        onClose={() => {
          setIsConcertModalOpen(false);
          setConcertVenue(null);
        }}
        onSubmit={handleCreateConcert}
        mode="create"
        bands={bands}
        defaultVenueId={concertVenue?._id}
        defaultVenueName={concertVenue?.name}
        onOpenVenueContact={(venueId) => {
          const venue = venues.find((v) => v._id === venueId) ?? concertVenue;
          if (!venue) return;
          handleOpenContact(venue);
        }}
      />

      {quickEditState && (
        <VenueFieldEditor
          venue={quickEditState.venue}
          field={quickEditState.field}
          position={quickEditState.position}
          onSave={handleQuickEditSave}
          onClose={() => setQuickEditState(null)}
          readonlyCoreFields={
            !user?.is_admin &&
            (quickEditState.venue.visibility === 'public' || quickEditState.venue.visibility === 'pending_public')
          }
          bands={bands}
        />
      )}

      <ConfirmDialog
        isOpen={isBatchDeleteConfirmOpen}
        onClose={() => setIsBatchDeleteConfirmOpen(false)}
        onConfirm={handleBatchDelete}
        title={`Delete ${selectedCount} ${selectedCount === 1 ? 'venue' : 'venues'}?`}
        message={
          allPagesSelected
            ? `All ${selectedCount} venues matching current filters will be permanently deleted. This cannot be undone.`
            : `${selectedCount} ${selectedCount === 1 ? 'venue' : 'venues'} will be permanently deleted. This cannot be undone.`
        }
        confirmLabel="Delete"
        danger
        isLoading={isBatchDeleting}
      />

      <ConfirmDialog
        isOpen={isResetFlagsConfirmOpen}
        onClose={() => setIsResetFlagsConfirmOpen(false)}
        onConfirm={handleResetFlags}
        title="Reset all flags?"
        message="Every flagged venue will be unflagged, including those outside the current filter."
        confirmLabel="Reset flags"
        danger
        isLoading={isResettingFlags}
      />

      {/* The pause picker, date-only: the level is fixed to "Not Now", which is what a pause is.
          One shared modal for the row menu and the selection. See docs/VENUE_PAUSE.md. */}
      {pauseModalRow && (
        <InterestModal
          isOpen
          dateOnly
          isOverall
          onClose={() => setPauseModalRow(null)}
          bandLabel={pauseModalRow.name}
          currentLevel="not_interested_for_now"
          currentComment={pauseModalRow.recontact?.reason || ''}
          currentRecontact={pauseModalRow.recontact ?? null}
          onSave={(_level, _comment, recontact) => handleRowRecontact(pauseModalRow, recontact ?? null)}
        />
      )}
      {isPauseBulkOpen && (
        <InterestModal
          isOpen
          dateOnly
          isOverall
          onClose={() => setIsPauseBulkOpen(false)}
          bandLabel={`${selectedCount} ${selectedCount === 1 ? 'venue' : 'venues'}`}
          currentLevel="not_interested_for_now"
          currentComment=""
          currentRecontact={null}
          onSave={(_level, _comment, recontact) => handleBatchRecontact(recontact ?? null)}
        />
      )}

      {/* Add to mailing list flow */}
      <AddVenuesToMailingListModal
        isOpen={mlStep === 'config'}
        onClose={() => setMlStep('closed')}
        venueCount={selectedCount}
        isResolving={mlResolving}
        onSubmit={handleMlConfigSubmit}
      />
      {mlStep === 'create' && (
        <MailingListFormModal
          bands={bands}
          initialContacts={mlContacts}
          onClose={() => setMlStep('closed')}
          onSaved={handleMlCreated}
        />
      )}
      <MailingListPickerModal
        isOpen={mlStep === 'existing'}
        onClose={() => setMlStep('closed')}
        onPick={handleMlPickExisting}
        isSubmitting={mlSubmitting}
      />
    </div>
  );
}
